import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Tooltip,
  Box,
  Badge,
  Chip,
  Avatar,
  Typography,
  Collapse,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';

import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import DomainIcon from '@mui/icons-material/Domain';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import Person2Outlined from '@mui/icons-material/Person2Outlined';
import LogoutIcon from '@mui/icons-material/Logout';
import RequestPage from '@mui/icons-material/RequestPage';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { Build, Campaign, FactCheck, Flight, Inventory, LocalActivity, Timelapse, WorkOutlineOutlined } from '@mui/icons-material';

import Wlogo from './w_logo.png';
import api, { clearAuthAndRedirect } from '../../services/api';
import { getSelectedPlatformTenant } from '../../services/platformTenantsApi';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { buildBackendUrl } from '../../config/env';
import { useTenant } from '../../tenancy/TenantProvider';
import { createRoleCapabilities, parseStoredList } from '../../utils/roleCapabilities';

function Sidebar({
  isMobile,
  mobileOpen,
  onMobileClose,
  expanded,
  drawerWidthExpanded = 240,
  drawerWidthCollapsed = 72,
}) {
  const location = useLocation();
  const theme = useTheme();
  const { resolvedMode } = useThemeMode();
  const { branding } = useTenant();

  const isPlatformAdmin = localStorage.getItem('is_platform_admin') === '1';
  const selectedPlatformTenant = getSelectedPlatformTenant();

  const storedRole = localStorage.getItem('role') || 'employee';
  const storedActualRole = localStorage.getItem('actual_role') || '';
  const permissions = parseStoredList('permissions');
  const employeeCode = String(localStorage.getItem('employee_code') || '').trim();
  const userName = localStorage.getItem('name') || 'User Name';
  const capabilities = createRoleCapabilities({
    role: storedRole,
    actualRole: storedActualRole,
    permissions,
    isPlatformAdmin,
    userName,
    employeeCode,
  });
  const {
    role,
    isSuperAdmin,
    roleLabel,
    hasEmployeeAccess,
    hasHrAdminAccess,
    hasHrTeamAccess,
    hasRecruiterAccess,
    hasManagerAccess,
    hasHodAccess,
    hasOperationsAccess,
    hasFinanceAccess,
    hasExecutiveAccess,
    hasManagerFamilyAccess,
    hasExecutiveFamilyAccess,
    hasRequestActionAccess,
    hasAppraisalAccess,
    hasPreApprovalAccess,
  } = capabilities;
  const canAccessCvReview = hasHrAdminAccess || hasRecruiterAccess;
  const PR = localStorage.getItem('pr') || '0';
  const avatarUrl = buildBackendUrl(localStorage.getItem('avatar_url'));
  const sidebarLogo = branding.logo_light_url || Wlogo;
  const darkMode = resolvedMode === 'dark';
  const navBrand = {
    navy: theme.palette.primary.dark,
    blue: theme.palette.primary.main,
    cyan: theme.palette.secondary.main,
    green: theme.palette.success.main,
  };
  // A calm near-solid navy plane with one soft top-left glow (anchored where the
  // logo/avatar sit). Dark mode sits one notch below palette navy so the rail
  // reads as its own plane against the app canvas — the boxShadow makes that seam
  // visible, so it must never be dropped.
  const sidebarBackground = darkMode
    ? `radial-gradient(680px 420px at 28px -40px, ${alpha(navBrand.blue, 0.32)}, transparent 70%), #0d1626`
    : `radial-gradient(680px 420px at 28px -40px, ${alpha(navBrand.blue, 0.55)}, transparent 70%), ${navBrand.navy}`;
  const sidebarBoxShadow = darkMode
    ? theme.hr?.shadow?.soft || theme.shadows[8]
    : `8px 0 24px ${alpha(navBrand.navy, 0.16)}`;

  const [assignedPendingCount, setAssignedPendingCount] = useState(0);
  const [assignedVacationCount, setAssignedVacationCount] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingExitEntryCount, setPendingExitEntryCount] = useState(0);
  const [appraisalTasksCount, setAppraisalTasksCount] = useState(0);
  const [preApprovalActionCount, setPreApprovalActionCount] = useState(0);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const requestsBadgeCount = Math.max(assignedPendingCount, pendingExitEntryCount);
  const vacationsBadgeCount = Math.max(assignedVacationCount, pendingRequestsCount);
  const appraisalBadgeCount = appraisalTasksCount;
  const preApprovalsBadgeCount = preApprovalActionCount;

  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [communicationsOpen, setCommunicationsOpen] = useState(false);

  const showLabels = isMobile || expanded;
  const currentWidth = expanded ? drawerWidthExpanded : drawerWidthCollapsed;

  const fetchAssignedPendingBreakdown = async () => {
    try {
      const res = await api.get('/hr-requests/assigned-pending-breakdown');
      setAssignedPendingCount(res.data?.total || 0);
      // The breakdown is keyed by request_type, so the pre-approval queue count
      // for the current user comes free from the same call.
      setPreApprovalActionCount(res.data?.pre_approval || 0);
    } catch (error) {
      console.error('Error fetching assigned pending requests breakdown:', error);
      setAssignedPendingCount(0);
      setPreApprovalActionCount(0);
    }
  };

  const fetchAssignedVacationBreakdown = async () => {
    try {
      const res = await api.get('/vacation-requests/assigned-pending-breakdown');
      setAssignedVacationCount(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching assigned pending vacation breakdown:', error);
      setAssignedVacationCount(0);
    }
  };

  const fetchPendingVacationCount = async () => {
    try {
      if ((hasManagerAccess || hasHodAccess || hasOperationsAccess) && !hasFinanceAccess && !hasExecutiveFamilyAccess) {
        const res = await api.get('/manager/vacation-requests/pending-count');
        setPendingRequestsCount(res.data.count || 0);
      } else if (hasHrAdminAccess) {
        const res = await api.get('/admin/vacation-requests/pending-count');
        setPendingRequestsCount(res.data.count || 0);
      } else if (hasExecutiveFamilyAccess) {
        const res = await api.get('/ceo/vacation-requests/pending-count');
        setPendingRequestsCount(res.data.count || 0);
      } else if (hasFinanceAccess) {
        const res = await api.get('/finance/vacation-requests/pending-count');
        setPendingRequestsCount(res.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching old pendingVacationCount:', error);
      setPendingRequestsCount(0);
    }
  };

  const fetchPendingExitEntryCount = async () => {
    try {
      if ((hasManagerAccess || hasHodAccess || hasOperationsAccess) && !hasFinanceAccess && !hasExecutiveFamilyAccess) {
        const res = await api.get('/manager/exit-entry/pending-count');
        setPendingExitEntryCount(res.data.count || 0);
      } else if (hasFinanceAccess) {
        const res = await api.get('/finance/exit-entry/pending-count');
        setPendingExitEntryCount(res.data.count || 0);
      } else if (hasHrAdminAccess) {
        const res = await api.get('/admin/exit-entry/pending-count');
        setPendingExitEntryCount(res.data.count || 0);
      } else if (hasExecutiveFamilyAccess) {
        const res = await api.get('/ceo/exit-entry/pending-count');
        setPendingExitEntryCount(res.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching pending exit-entry requests:', error);
      setPendingExitEntryCount(0);
    }
  };

  const fetchAppraisalTasksCount = async () => {
    try {
      const res = await api.get('/performance/appraisals/stats/my-tasks');
      const raw = Number(res?.data?.inbox_count ?? res?.data?.my_tasks_pending_current ?? 0);
      setAppraisalTasksCount(Number.isFinite(raw) ? raw : 0);
    } catch (error) {
      console.error('Error fetching appraisal task counts:', error);
      setAppraisalTasksCount(0);
    }
  };

  const fetchNotificationUnreadCount = async () => {
    try {
      const res = await api.get('/notifications');
      const rows = Array.isArray(res.data) ? res.data : [];
      setNotificationUnreadCount(rows.filter((item) => !item.read).length);
    } catch (error) {
      console.error('Error fetching notification unread count:', error);
      setNotificationUnreadCount(0);
    }
  };

  useEffect(() => {
    const requestActionRoles = hasRequestActionAccess;
    const appraisalActionRoles = hasAppraisalAccess;

    if (requestActionRoles) {
      fetchAssignedPendingBreakdown();
      fetchAssignedVacationBreakdown();
      fetchPendingVacationCount();
      fetchPendingExitEntryCount();
    } else if (hasPreApprovalAccess) {
      // Pre-approval approvers (HOD / Operations / CEO / Executive / HR) who
      // aren't in the broader request-action set still need the pre-approval count.
      fetchAssignedPendingBreakdown();
    }

    if (appraisalActionRoles) {
      fetchAppraisalTasksCount();
    }

    let intervalId;
    if (requestActionRoles || appraisalActionRoles || hasPreApprovalAccess) {
      intervalId = window.setInterval(() => {
        if (requestActionRoles) {
          fetchAssignedPendingBreakdown();
          fetchAssignedVacationBreakdown();
          fetchPendingVacationCount();
          fetchPendingExitEntryCount();
        } else if (hasPreApprovalAccess) {
          fetchAssignedPendingBreakdown();
        }
        if (appraisalActionRoles) {
          fetchAppraisalTasksCount();
        }
      }, 60000);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, role]);

  useEffect(() => {
    fetchNotificationUnreadCount();
    const intervalId = window.setInterval(fetchNotificationUnreadCount, 60000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    window.refreshAssignedPendingCount = fetchAssignedPendingBreakdown;
    window.refreshAssignedPendingVacationCount = fetchAssignedVacationBreakdown;
    window.refreshPendingRequestsCount = fetchPendingVacationCount;
    window.refreshPendingExitEntryCount = fetchPendingExitEntryCount;
    window.refreshAppraisalTasksCount = fetchAppraisalTasksCount;
    window.refreshNotificationUnreadCount = fetchNotificationUnreadCount;

    return () => {
      delete window.refreshAssignedPendingCount;
      delete window.refreshAssignedPendingVacationCount;
      delete window.refreshPendingRequestsCount;
      delete window.refreshPendingExitEntryCount;
      delete window.refreshAppraisalTasksCount;
      delete window.refreshNotificationUnreadCount;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubMenuToggle = (subKey) => {
    if (subKey === 'announcement') setAnnouncementOpen(!announcementOpen);
    else if (subKey === 'holiday') setHolidayOpen(!holidayOpen);
    else if (subKey === 'vacation') setVacationOpen(!vacationOpen);
    else if (subKey === 'performance') setPerformanceOpen(!performanceOpen);
    else if (subKey === 'communications') setCommunicationsOpen(!communicationsOpen);
  };

  const isSubMenuOpen = (subKey) => {
    if (subKey === 'announcement') return announcementOpen;
    if (subKey === 'holiday') return holidayOpen;
    if (subKey === 'vacation') return vacationOpen;
    if (subKey === 'performance') return performanceOpen;
    if (subKey === 'communications') return communicationsOpen;
    return false;
  };

  const notificationCenterItem = {
    text: 'Notification Center',
    icon: (
      <Badge color="error" badgeContent={notificationUnreadCount} invisible={notificationUnreadCount === 0}>
        <NotificationsActiveIcon />
      </Badge>
    ),
    path: '/notifications',
    badgeCount: notificationUnreadCount,
  };
  const managerDashboardLabel = hasHodAccess
    ? 'HOD Dashboard'
    : hasOperationsAccess
      ? 'Operations Dashboard'
      : hasFinanceAccess
        ? 'Finance Dashboard'
        : 'Manager Dashboard';
  const executiveDashboardLabel = hasExecutiveAccess ? 'Executive Dashboard' : 'CEO Dashboard';

  const hrAdminNavItems = [
    // Overview
    { section: 'Overview', text: 'Admin Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { section: 'Overview', text: 'Attendances', icon: <Timelapse />, path: '/admin/attendances' },

    // People
    { section: 'People', text: 'Candidates', icon: <GroupIcon />, path: '/recruiter/candidates' },
    { section: 'People', text: 'Employee CV', icon: <DescriptionOutlinedIcon />, path: '/cv/review' },
    { section: 'People', text: 'Manage Users', icon: <GroupIcon />, path: '/admin/users' },
    { section: 'People', text: 'Departments', icon: <DomainIcon />, path: '/admin/departments' },
    { section: 'People', text: 'Project', icon: <Build />, path: '/admin/projects' },
    { section: 'People', text: 'Job Offers', icon: <WorkOutlineOutlined />, path: '/hr/job-offers' },

    // Workflows
    {
      section: 'Workflows',
      text: 'Performance',
      icon: (
        <Badge color="error" badgeContent={appraisalBadgeCount} invisible={appraisalBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      badgeCount: appraisalBadgeCount,
      subKey: 'performance',
      subItems: [
        { text: 'My appraisal tasks', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my-tasks', badgeCount: appraisalBadgeCount },
        { text: 'My appraisals', icon: <RequestPage />, path: '/performance/appraisals/my' },
        { text: 'Manage appraisals', icon: <GroupIcon />, path: '/performance/appraisals' },
        { text: 'Appraisals report', icon: <Timelapse />, path: '/performance/appraisals/report' },
      ],
    },
    {
      section: 'Workflows',
      text: 'Requests',
      icon: (
        <Badge color="error" badgeContent={requestsBadgeCount} invisible={requestsBadgeCount === 0}>
          <RequestPage />
        </Badge>
      ),
      path: '/all-requests',
    },
    {
      section: 'Workflows',
      text: 'Pre-Approvals',
      icon: (
        <Badge color="error" badgeContent={preApprovalsBadgeCount} invisible={preApprovalsBadgeCount === 0}>
          <FactCheck />
        </Badge>
      ),
      path: '/pre-approvals',
      badgeCount: preApprovalsBadgeCount,
    },
    {
      section: 'Workflows',
      text: 'Vacations',
      icon: (
        <Badge color="error" badgeContent={vacationsBadgeCount} invisible={vacationsBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      path: '/admin/vacation-requests/all',
    },
    {
      section: 'Workflows',
      text: 'Communications',
      icon: <Campaign />,
      subKey: 'communications',
      subItems: [
        { text: 'Email Campaigns', icon: <Campaign />, path: '/admin/communications/email-campaigns' },
        { text: 'Mobile Notifications', icon: <NotificationsActiveIcon />, path: '/admin/communications/mobile-notifications' },
      ],
    },
    { section: 'Workflows', text: 'Surveys', icon: <AssignmentTurnedInIcon />, path: '/admin/surveys' },

    // Workspace
    { section: 'Workspace', text: 'Assets', icon: <Inventory />, path: '/assets' },
    { section: 'Workspace', ...notificationCenterItem },
    { section: 'Workspace', text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
    { section: 'Workspace', text: 'Insurance Policies', icon: <LocalActivity />, path: '/admin/insurance-policies' },
    { section: 'Workspace', text: 'HR Assistant Policies', icon: <MenuBookOutlinedIcon />, path: '/admin/policies' },
    { section: 'Workspace', text: 'Assistant Training', icon: <SchoolOutlinedIcon />, path: '/admin/assistant-training' },
  ];

  const superAdminNavItems = [
    { section: 'Platform', text: 'Super Admin Dashboard', icon: <DashboardIcon />, path: '/super-admin/dashboard' },
    { section: 'Platform', text: 'Tenant Management', icon: <DomainIcon />, path: '/super-admin/platform/tenants' },
    { section: 'Platform', text: 'Request Reminders', icon: <NotificationsActiveOutlinedIcon />, path: '/super-admin/platform/reminders' },
    { section: 'Platform', text: 'Data Import', icon: <UploadFileOutlinedIcon />, path: '/super-admin/platform/data-import' },
    { section: 'Platform', text: 'Role Permissions', icon: <AdminPanelSettingsOutlinedIcon />, path: '/super-admin/application-roles' },
    { section: 'Platform', text: 'Zone Tracking', icon: <LocalActivity />, path: '/super-admin/zone-tracking' },
    { section: 'Platform', text: 'Appraisal Workflows', icon: <Build />, path: '/super-admin/appraisal-workflows' },
    { section: 'Platform', text: 'Request Workflows', icon: <Build />, path: '/super-admin/request-workflows' },
    { section: 'Platform', text: 'System Errors', icon: <ReportProblemOutlinedIcon />, path: '/super-admin/platform/errors' },
    ...hrAdminNavItems,
  ];

  // Recruiter = full employee self-service experience + recruiter tools.
  const recruiterNavItems = [
    { text: 'Employee Dashboard', icon: <DashboardIcon />, path: '/employee/dashboard' },
    { text: 'My Requests', icon: <RequestPage />, path: '/employee/vacation-requests' },
    {
      text: 'Requests',
      icon: (
        <Badge color="error" badgeContent={requestsBadgeCount} invisible={requestsBadgeCount === 0}>
          <RequestPage />
        </Badge>
      ),
      path: '/all-requests',
    },
    { text: 'New Request', icon: <RequestPage />, path: '/hr-requests/new' },
    {
      text: 'Pre-Approvals',
      icon: (
        <Badge color="error" badgeContent={preApprovalsBadgeCount} invisible={preApprovalsBadgeCount === 0}>
          <FactCheck />
        </Badge>
      ),
      path: '/pre-approvals',
      badgeCount: preApprovalsBadgeCount,
    },
    { text: 'Candidates', icon: <GroupIcon />, path: '/recruiter/candidates' },
    { text: 'Employee CV', icon: <DescriptionOutlinedIcon />, path: '/cv/review' },
    {
      text: 'Appraisals',
      icon: (
        <Badge color="error" badgeContent={appraisalBadgeCount} invisible={appraisalBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      badgeCount: appraisalBadgeCount,
      subKey: 'performance',
      subItems: [
        { text: 'My appraisal tasks', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my-tasks', badgeCount: appraisalBadgeCount },
        { text: 'My appraisals', icon: <RequestPage />, path: '/performance/appraisals/my' },
      ],
    },
    { text: 'My CV', icon: <DescriptionOutlinedIcon />, path: '/cv/me' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    notificationCenterItem,
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  const hrTeamNavItems = [
    {
      text: 'Requests',
      icon: (
        <Badge color="error" badgeContent={requestsBadgeCount} invisible={requestsBadgeCount === 0}>
          <RequestPage />
        </Badge>
      ),
      path: '/all-requests',
      badgeCount: requestsBadgeCount,
    },
    { text: 'New Request', icon: <RequestPage />, path: '/hr-requests/new' },
    {
      text: 'Pre-Approvals',
      icon: (
        <Badge color="error" badgeContent={preApprovalsBadgeCount} invisible={preApprovalsBadgeCount === 0}>
          <FactCheck />
        </Badge>
      ),
      path: '/pre-approvals',
      badgeCount: preApprovalsBadgeCount,
    },
    { text: 'Job Offers', icon: <WorkOutlineOutlined />, path: '/hr/job-offers' },
    {
      text: 'Appraisals',
      icon: (
        <Badge color="error" badgeContent={appraisalBadgeCount} invisible={appraisalBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      badgeCount: appraisalBadgeCount,
      subKey: 'performance',
      subItems: [
        { text: 'My appraisal tasks', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my-tasks', badgeCount: appraisalBadgeCount },
        { text: 'My appraisals', icon: <RequestPage />, path: '/performance/appraisals/my' },
      ],
    },
    { text: 'My CV', icon: <DescriptionOutlinedIcon />, path: '/cv/me' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    notificationCenterItem,
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  const managerNavItems = [
    { text: managerDashboardLabel, icon: <DashboardIcon />, path: '/manager/dashboard' },
    {
      text: 'Requests',
      icon: (
        <Badge color="error" badgeContent={requestsBadgeCount} invisible={requestsBadgeCount === 0}>
          <RequestPage />
        </Badge>
      ),
      path: '/all-requests',
    },
    {
      text: 'Appraisals',
      icon: (
        <Badge color="error" badgeContent={appraisalBadgeCount} invisible={appraisalBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      badgeCount: appraisalBadgeCount,
      subKey: 'performance',
      subItems: [
        { text: 'My appraisal tasks', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my-tasks', badgeCount: appraisalBadgeCount },
        { text: 'My appraisals', icon: <RequestPage />, path: '/performance/appraisals/my' },
        ...(hasFinanceAccess || hasExecutiveFamilyAccess || hasHodAccess || hasOperationsAccess
          ? [{ text: 'Appraisals report', icon: <Timelapse />, path: '/performance/appraisals/report' }]
          : []),
      ],
    },
    {
      text: 'Vacations',
      icon: (
        <Badge color="error" badgeContent={vacationsBadgeCount} invisible={vacationsBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      path: '/manager/vacation-requests',
    },
    ...(hasHodAccess || hasOperationsAccess
      ? [
          {
            text: 'Pre-Approvals',
            icon: (
              <Badge color="error" badgeContent={preApprovalsBadgeCount} invisible={preApprovalsBadgeCount === 0}>
                <FactCheck />
              </Badge>
            ),
            path: '/pre-approvals',
            badgeCount: preApprovalsBadgeCount,
          },
        ]
      : []),
    { text: 'My CV', icon: <DescriptionOutlinedIcon />, path: '/cv/me' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    notificationCenterItem,
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  const ceoNavItems = [
    { text: executiveDashboardLabel, icon: <DashboardIcon />, path: '/ceo/dashboard' },
    {
      text: 'Requests',
      icon: (
        <Badge color="error" badgeContent={requestsBadgeCount} invisible={requestsBadgeCount === 0}>
          <RequestPage />
        </Badge>
      ),
      path: '/all-requests',
    },
    {
      text: 'Appraisals',
      icon: (
        <Badge color="error" badgeContent={appraisalBadgeCount} invisible={appraisalBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      badgeCount: appraisalBadgeCount,
      subKey: 'performance',
      subItems: [
        { text: 'My appraisal tasks', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my-tasks', badgeCount: appraisalBadgeCount },
        { text: 'My appraisals', icon: <RequestPage />, path: '/performance/appraisals/my' },
        { text: 'Appraisals report', icon: <Timelapse />, path: '/performance/appraisals/report' },
      ],
    },
    {
      text: 'Vacations',
      icon: (
        <Badge color="error" badgeContent={vacationsBadgeCount} invisible={vacationsBadgeCount === 0}>
          <AssignmentTurnedInIcon />
        </Badge>
      ),
      path: '/manager/vacation-requests',
    },
    {
      text: 'Pre-Approvals',
      icon: (
        <Badge color="error" badgeContent={preApprovalsBadgeCount} invisible={preApprovalsBadgeCount === 0}>
          <FactCheck />
        </Badge>
      ),
      path: '/pre-approvals',
      badgeCount: preApprovalsBadgeCount,
    },
    notificationCenterItem,
    { text: 'My CV', icon: <DescriptionOutlinedIcon />, path: '/cv/me' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  const employeeNavItems = [
    { text: 'Employee Dashboard', icon: <DashboardIcon />, path: '/employee/dashboard' },
    { text: 'My Requests', icon: <RequestPage />, path: '/employee/vacation-requests' },
    { text: 'Appraisals', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my' },
    { text: 'My CV', icon: <DescriptionOutlinedIcon />, path: '/cv/me' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    notificationCenterItem,
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  const employeeNavItemsPR = [
    { text: 'Employee Dashboard', icon: <DashboardIcon />, path: '/employee/dashboard' },
    { text: 'My Requests', icon: <RequestPage />, path: '/employee/vacation-requests' },
    { text: 'Appraisals', icon: <AssignmentTurnedInIcon />, path: '/performance/appraisals/my' },
    { text: 'Requests Exit/Entry', icon: <Flight />, path: '/employee/exit-entry/pr' },
    { text: 'My Surveys', icon: <AssignmentTurnedInIcon />, path: '/my/surveys' },
    notificationCenterItem,
    { text: 'Profile', icon: <Person2Outlined />, path: '/profile' },
  ];

  let navItems = employeeNavItems;

  if (isSuperAdmin) navItems = superAdminNavItems;
  else if (hasHrAdminAccess) navItems = hrAdminNavItems;
  else if (hasRecruiterAccess) navItems = recruiterNavItems;
  else if (hasHrTeamAccess) navItems = hrTeamNavItems;
  else if (hasExecutiveFamilyAccess) navItems = ceoNavItems;
  else if (hasManagerFamilyAccess) navItems = managerNavItems;
  else if (hasEmployeeAccess && PR === '1') navItems = employeeNavItemsPR;

  if (canAccessCvReview && !navItems.some((item) => item.path === '/cv/review')) {
    navItems = [
      ...navItems,
      { text: 'Employee CV', icon: <DescriptionOutlinedIcon />, path: '/cv/review' },
    ];
  }

  // Only the long role lists (HR admin / super admin) carry `section` labels;
  // shorter role menus stay flat so we never stamp eyebrows on a 6-item list.
  const useSections = navItems.length > 8 && navItems.some((item) => item.section);

  // Tiny structural group label. Sentence case in the data, uppercased via CSS
  // only, so it reads as a navigation label rather than shouting copy. Hidden on
  // the 72px rail (replaced by a hairline divider — see renderNavItems).
  const sectionLabelSx = {
    px: 1.4,
    pb: 0.5,
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: alpha('#ffffff', 0.4),
    userSelect: 'none',
    lineHeight: 1.6,
  };

  // Shared resting/hover geometry for every nav row (and the submenu header), so
  // the two render paths can't drift. Active styling is layered on in renderMenuItem.
  const itemBaseSx = (nested = false) => ({
    position: 'relative',
    color: alpha('#ffffff', nested ? 0.6 : 0.74),
    justifyContent: showLabels ? 'initial' : 'center',
    mx: showLabels ? 0.6 : 0,
    px: showLabels ? 1.4 : 0,
    py: nested ? 0.7 : 0.85,
    pl: nested ? (showLabels ? 3.4 : 0) : showLabels ? 1.4 : 0,
    borderRadius: 1.5,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '& .MuiListItemIcon-root': {
      color: 'inherit',
      minWidth: 0,
      mr: showLabels ? 1.3 : 0,
      justifyContent: 'center',
      opacity: 0.9,
      '& svg': { fontSize: 20 },
      // Punch the count badge cleanly out of the rail surface + tabular figures.
      '& .MuiBadge-badge': {
        fontVariantNumeric: 'tabular-nums',
        boxShadow: `0 0 0 2px ${darkMode ? '#0d1626' : navBrand.navy}`,
      },
    },
    '&:hover': {
      backgroundColor: alpha('#ffffff', darkMode ? 0.06 : 0.08),
      color: '#ffffff',
      transform: 'none', // override the global MuiListItemButton translateX(2px)
      '& .MuiListItemIcon-root': { opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  });

  const handleLogout = () => {
    clearAuthAndRedirect();
  };

  const renderMenuItem = (item, nested = false) => {
    const closeIfMobile = () => {
      if (isMobile) onMobileClose?.();
    };
    const badgeValue = Number(item?.badgeCount || 0);
    const badgeLabel = badgeValue > 99 ? '99+' : badgeValue;

    // Determine "active" by comparing both pathname and any query params the
    // sidebar item carries, so query-specific sibling links do not both highlight.
    const itemActive = (() => {
      if (!item?.path) return false;
      const [itemPath, itemQuery = ''] = String(item.path).split('?');
      if (location.pathname !== itemPath) return false;
      if (!itemQuery) {
        // A "bare" sidebar item is only active when no sibling-defining query
        // params are present in the URL. Otherwise the bare item would also
        // light up while a more specific sibling matches.
        const currentParams = new URLSearchParams(location.search);
        if (currentParams.get('view') || currentParams.get('request_type')) {
          return false;
        }
        return true;
      }
      const itemParams = new URLSearchParams(itemQuery);
      const currentParams = new URLSearchParams(location.search);
      for (const [key, value] of itemParams.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    })();

    return (
      // Tooltip only when collapsed — when the sidebar is expanded the
      // label is already visible and a hover-tooltip floats over the page
      // content next to the rail, obscuring page headings.
      <Tooltip key={`${nested ? 'sub-' : 'main-'}${item.text}`} title={!showLabels ? item.text : ''} placement="right">
        <ListItemButton
          component={NavLink}
          to={item.path}
          // We can't reliably suppress NavLink's auto-applied `active` class
          // through MUI's ListItemButton wrapper, so we use a private class
          // name and target it from sx. That way sibling NavLinks that share
          // a pathname only show active when our query-aware `itemActive` agrees.
          className={itemActive ? 'sidebar-route-active' : ''}
          onClick={closeIfMobile}
          sx={{
            ...itemBaseSx(nested),
            // One accent only: a 3px green rail drawn as a ::before pseudo (NOT
            // boxShadow) so it can't override the theme's global focus-visible ring.
            '&.sidebar-route-active': {
              backgroundColor: alpha('#ffffff', darkMode ? 0.1 : 0.12),
              color: '#ffffff',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 7,
                bottom: 7,
                width: 3,
                borderRadius: '0 3px 3px 0',
                backgroundColor: navBrand.green,
              },
              '& .MuiListItemText-primary': { fontWeight: 700 },
              '& .MuiListItemIcon-root': { color: navBrand.green, opacity: 1 },
              // Count chip stays red (alert semantics survive the active row).
              '& .MuiChip-root': { backgroundColor: theme.palette.error.main, color: '#ffffff' },
              '&:hover': { backgroundColor: alpha('#ffffff', darkMode ? 0.13 : 0.16) },
              // Keep a visible focus ring for keyboard users on the active row.
              '&.Mui-focusVisible': {
                boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, darkMode ? 0.42 : 0.28)}`,
              },
            },
          }}
        >
          {item.icon ? (
            <ListItemIcon>{item.icon}</ListItemIcon>
          ) : null}
          {showLabels ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: nested ? '0.8rem' : '0.86rem',
                  fontWeight: nested ? 500 : 600,
                  noWrap: true,
                }}
              />
              {badgeValue > 0 ? (
                <Chip
                  label={badgeLabel}
                  size="small"
                  color="error"
                  sx={{
                    height: 18,
                    minWidth: 18,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '999px',
                    boxShadow: 'none',
                    '& .MuiChip-label': { px: 0.6, fontVariantNumeric: 'tabular-nums' },
                  }}
                />
              ) : null}
            </Box>
          ) : null}
        </ListItemButton>
      </Tooltip>
    );
  };

  const renderSubMenuGroup = (nav) => {
    const navBadgeValue = Number(nav?.badgeCount || 0);
    const navBadgeLabel = navBadgeValue > 99 ? '99+' : navBadgeValue;
    const subIsOpen = isSubMenuOpen(nav.subKey || '');

    return (
      <React.Fragment key={`group-${nav.text}`}>
        <ListItemButton
          onClick={() => {
            handleSubMenuToggle(nav.subKey);
          }}
          sx={{
            ...itemBaseSx(false),
            '& .expand-caret': { color: alpha('#ffffff', 0.5), fontSize: 18 },
          }}
        >
          {nav.icon ? <ListItemIcon>{nav.icon}</ListItemIcon> : null}

          {showLabels ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <ListItemText primary={nav.text} primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 600, noWrap: true }} />
              {navBadgeValue > 0 ? (
                <Chip
                  label={navBadgeLabel}
                  size="small"
                  color="error"
                  sx={{
                    height: 18,
                    minWidth: 18,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    borderRadius: '999px',
                    boxShadow: 'none',
                    '& .MuiChip-label': { px: 0.6, fontVariantNumeric: 'tabular-nums' },
                  }}
                />
              ) : null}
            </Box>
          ) : null}
          {showLabels ? (subIsOpen ? <ExpandLess className="expand-caret" /> : <ExpandMore className="expand-caret" />) : null}
        </ListItemButton>

        <Collapse in={subIsOpen && showLabels} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              position: 'relative',
              // Faint guide rail connecting children to their parent group.
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 22,
                top: 4,
                bottom: 6,
                width: '1px',
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            {nav.subItems?.map((subItem) => renderMenuItem(subItem, true))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const renderNavItems = () => {
    let prevSection = null;
    return navItems.map((nav, idx) => {
      // Emit a group label (expanded) or a hairline divider (collapsed rail) the
      // first time a new section appears, so the grouping rhythm survives both modes.
      const sectionNode =
        useSections && nav.section && nav.section !== prevSection
          ? showLabels
            ? (
                <Typography key={`sec-${nav.section}`} component="div" sx={{ ...sectionLabelSx, pt: idx === 0 ? 0.75 : 2 }}>
                  {nav.section}
                </Typography>
              )
            : idx === 0
              ? null
              : <Divider key={`sec-${nav.section}`} sx={{ borderColor: alpha('#ffffff', 0.08), mx: 1.4, my: 0.75 }} />
          : null;
      if (nav.section) prevSection = nav.section;

      const body = nav.subItems?.length ? renderSubMenuGroup(nav) : renderMenuItem(nav);

      return (
        <React.Fragment key={`nav-${nav.text}`}>
          {sectionNode}
          {body}
        </React.Fragment>
      );
    });
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: { lg: currentWidth },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? 292 : currentWidth,
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.short,
          }),
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderRight: 'none',
          color: '#ffffff',
          background: sidebarBackground,
          boxShadow: sidebarBoxShadow,
          // Desktop: pin the rail to the viewport so the nav Box below owns the
          // ONLY scroll region. Left at the docked default (position: relative +
          // MUI's height: 100%) the paper stretches to the FULL page height on
          // long pages, so the nav never overflows — and with nothing to scroll
          // under the pointer, hovering the sidebar and spinning the wheel does
          // nothing. Mobile keeps the temporary-drawer (Modal) default.
          ...(isMobile
            ? { position: 'relative' }
            : { position: 'sticky', top: 0, height: '100vh' }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        },
      }}
    >
      <Toolbar
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showLabels ? 'space-between' : 'center',
          minHeight: { xs: 64, sm: 70 },
          px: 1,
          borderBottom: `1px solid ${alpha('#ffffff', darkMode ? 0.07 : 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{
              width: showLabels ? 126 : 0,
              overflow: 'hidden',
              transition: theme.transitions.create(['width', 'opacity'], { duration: theme.transitions.duration.short }),
              opacity: showLabels ? 1 : 0,
            }}
          >
            <img src={sidebarLogo} alt={`${branding.short_name || 'Tenant'} logo`} style={{ width: '100%', display: 'block', objectFit: 'contain' }} />
          </Box>

          {/* On mobile: close by tapping backdrop or selecting a menu item. */}
        </Box>
      </Toolbar>

      {/* Account zone — non-scrolling. A quiet inset chip when expanded; a bare
          centered avatar with a neutral hairline on the rail (green is reserved
          for active wayfinding, not a static avatar). */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: showLabels ? 1.2 : 0,
            mx: showLabels ? 1 : 0,
            my: 1,
            px: showLabels ? 1.2 : 0,
            py: showLabels ? 1 : 0.5,
            borderRadius: 2,
            justifyContent: showLabels ? 'flex-start' : 'center',
            bgcolor: showLabels ? alpha('#ffffff', darkMode ? 0.05 : 0.07) : 'transparent',
          }}
        >
          <Tooltip title={!showLabels ? userName : ''} placement="right">
            <Avatar
              src={avatarUrl || undefined}
              alt={userName}
              sx={{
                width: showLabels ? 34 : 30,
                height: showLabels ? 34 : 30,
                border: `1px solid ${alpha('#ffffff', 0.22)}`,
                boxShadow: 'none',
              }}
            />
          </Tooltip>

          {showLabels ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff' }} noWrap>
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.62), textTransform: 'capitalize' }} noWrap>
                {roleLabel}
              </Typography>
              {isSuperAdmin ? (
                <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.5), display: 'block' }} noWrap>
                  {selectedPlatformTenant?.name
                    ? `Tenant: ${selectedPlatformTenant.name}`
                    : 'Tenant: not selected'}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* The nav is the only scroll region (thin, mode-aware scrollbar; scroll
          contained so the wheel doesn't chain to the page). */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Don't chain wheel/touch scroll to the page, and use momentum on touch.
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          // Smooth programmatic scrolls (e.g. focusing the active route), off under reduced motion.
          scrollBehavior: 'smooth',
          '@media (prefers-reduced-motion: reduce)': { scrollBehavior: 'auto' },
          // Floating inset thumb: 6px footprint preserved (so the collapsed-rail
          // centering is untouched), but the visible thumb reads ~4px with breathing room.
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#ffffff', darkMode ? 0.16 : 0.22),
            borderRadius: 999,
            border: '1px solid transparent',
            backgroundClip: 'content-box',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#ffffff', darkMode ? 0.26 : 0.34),
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha('#ffffff', 0.22)} transparent`,
        }}
      >
        <List sx={{ py: 1, px: 0 }}>{renderNavItems()}</List>
      </Box>

      <Box sx={{ flexShrink: 0, py: 1, borderTop: `1px solid ${alpha('#ffffff', darkMode ? 0.07 : 0.1)}` }}>
        <Tooltip title={!showLabels ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              color: alpha('#ffffff', 0.74),
              justifyContent: showLabels ? 'initial' : 'center',
              mx: showLabels ? 0.6 : 0,
              px: showLabels ? 1.4 : 0,
              py: 0.85,
              borderRadius: 1.5,
              transition: theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.14),
                color: theme.palette.error.light,
                transform: 'none',
                '& .MuiListItemIcon-root': { color: theme.palette.error.light },
              },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 0, mr: showLabels ? 1.3 : 0, justifyContent: 'center', '& svg': { fontSize: 20 } }}>
              <LogoutIcon />
            </ListItemIcon>
            {showLabels ? <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: 600 }} /> : null}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
